import type {ComposerMode} from "./composerMode";

/**
 * How the home panel turns between sending and asking for files (kitecorp/transferify-ui#3140, owner
 * 2026-10-01: "clicking 'request files' should slide on top from right to left the new UI ... clicking 'send
 * files' should reverse the UI animation"). React-free: the panel's hook measures heights and runs the
 * animation; this says what is where.
 *
 * At rest one composer shows. Turning slides the request composer in over the send one from the right, and
 * back out to the right off it, while the send composer stays where it is. The panel's height runs from the
 * height it had to the new composer's over the same time, so nothing below it jumps. A reader who asks for
 * reduced motion gets the plain swap.
 */
export type ComposerSlide = {kind: "settled"; mode: ComposerMode} | {kind: "sliding"; to: ComposerMode};

/** How long the request composer takes to cross the panel, either way. */
export const COMPOSER_SLIDE_MS = 320;

/** Where the send composer sits: in the panel's flow, beneath the request composer sliding in, or gone. */
export type SendPlacement = "flow" | "beneath" | "hidden";

/** Where the request composer sits: in the panel's flow, sliding in over the send one, sliding out, or gone. */
export type RequestPlacement = "flow" | "slidingIn" | "slidingOut" | "hidden";

/** The slide that shows `to` from `current`; asked mid-slide for the other composer, it turns round. */
export function slideTo(current: ComposerSlide, to: ComposerMode, reducedMotion: boolean): ComposerSlide {
	const showing = current.kind === "settled" ? current.mode : current.to;
	if (showing === to) return current;
	return reducedMotion ? {kind: "settled", mode: to} : {kind: "sliding", to};
}

/** The rest a slide ends on. */
export function settleSlide(slide: ComposerSlide): ComposerSlide {
	return slide.kind === "settled" ? slide : {kind: "settled", mode: slide.to};
}

/**
 * Where each composer sits. While sliding, the composer being turned TO is in the panel's flow, so the
 * panel's new height is its height; the request composer is always the one on top, moving.
 */
export function composerPlacement(slide: ComposerSlide): {send: SendPlacement; request: RequestPlacement} {
	if (slide.kind === "settled") {
		return slide.mode === "send" ? {send: "flow", request: "hidden"} : {send: "hidden", request: "flow"};
	}
	return slide.to === "request" ? {send: "beneath", request: "slidingIn"} : {send: "flow", request: "slidingOut"};
}
